import React, { useState, useEffect } from 'react';
import { 
  Settings, Save, AlertCircle, CheckCircle, Plus, Trash2, Edit, X,
  Calendar, DollarSign, Users, Layers, Globe, Bell, Shield, Zap,
  ChevronDown, ChevronRight, ToggleLeft, ToggleRight, Eye, EyeOff,
  Award, Building, Code, Brain, Link, Clock, Megaphone, Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminConfigPanel = ({ token }) => {
  const [activeSection, setActiveSection] = useState('platform');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Config states
  const [platformConfig, setPlatformConfig] = useState(null);
  const [iaplConfig, setIaplConfig] = useState(null);
  const [banners, setBanners] = useState([]);
  const [adminRoles, setAdminRoles] = useState({ admins: [], role_definitions: {} });
  
  // Modal states
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [institutionLogos, setInstitutionLogos] = useState([]);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [editingLogo, setEditingLogo] = useState(null);
  const [logoForm, setLogoForm] = useState({ logo_url: '', alt_text: '' });
  const [logoSaving, setLogoSaving] = useState(false);
  const [bannerForm, setBannerForm] = useState({
    title: '',
    message: '',
    type: 'info',
    cta_text: '',
    cta_link: '',
    start_date: '',
    end_date: '',
    target_modules: ['all'],
    priority: 1,
    is_active: true
  });

  useEffect(() => {
    fetchAllConfigs();
  }, []);

  const fetchAllConfigs = async () => {
    setLoading(true);
    try {
      const [platformRes, iaplRes, bannersRes, rolesRes, logosRes] = await Promise.all([
        axios.get(`${API}/admin/super-config/platform`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/super-config/iapl`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/super-config/banners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/super-config/roles`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/super-config/institution-logos`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setPlatformConfig(platformRes.data.config);
      setIaplConfig(iaplRes.data.config);
      setBanners(bannersRes.data.banners || []);
      setAdminRoles(rolesRes.data);
      setInstitutionLogos(logosRes.data.logos || []);
    } catch (err) {
      toast.error('Failed to fetch configuration');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const savePlatformConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/super-config/platform`, platformConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Platform configuration saved');
    } catch (err) {
      toast.error('Failed to save platform configuration');
    } finally {
      setSaving(false);
    }
  };

  const saveIaplConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/super-config/iapl`, iaplConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('IAPL configuration saved');
    } catch (err) {
      toast.error('Failed to save IAPL configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBanner = async () => {
    try {
      if (editingBanner) {
        await axios.put(`${API}/admin/super-config/banners/${editingBanner.id}`, bannerForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner updated');
      } else {
        await axios.post(`${API}/admin/super-config/banners`, bannerForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner created');
      }
      setShowBannerModal(false);
      setEditingBanner(null);
      resetBannerForm();
      fetchAllConfigs();
    } catch (err) {
      toast.error('Failed to save banner');
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await axios.delete(`${API}/admin/super-config/banners/${bannerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner deleted');
      fetchAllConfigs();
    } catch (err) {
      toast.error('Failed to delete banner');
    }
  };

  const resetBannerForm = () => {
    setBannerForm({
      title: '',
      message: '',
      type: 'info',
      cta_text: '',
      cta_link: '',
      start_date: '',
      end_date: '',
      target_modules: ['all'],
      priority: 1,
      is_active: true
    });
  };

  const openEditBanner = (banner) => {
    setEditingBanner(banner);
    setBannerForm({
      title: banner.title || '',
      message: banner.message || '',
      type: banner.type || 'info',
      cta_text: banner.cta_text || '',
      cta_link: banner.cta_link || '',
      start_date: banner.start_date || '',
      end_date: banner.end_date || '',
      target_modules: banner.target_modules || ['all'],
      priority: banner.priority || 1,
      is_active: banner.is_active !== false
    });
    setShowBannerModal(true);
  };

  const sections = [
    { id: 'platform', label: 'Platform Settings', icon: Globe },
    { id: 'pricing', label: 'Pricing & Fees', icon: DollarSign },
    { id: 'domains', label: 'Domains & Workshops', icon: Layers },
    { id: 'modules', label: 'Module Control', icon: Zap },
    { id: 'iapl', label: 'AI League (IAPL)', icon: Brain },
    { id: 'banners', label: 'Banner Ads', icon: Megaphone },
    { id: 'institution-logos', label: 'Institution Logos', icon: ImageIcon },
    { id: 'roles', label: 'Access Control', icon: Shield }
  ];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-admin-config">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Settings className="animate-spin-slow" size={28} />
          <h2 className="text-2xl font-bold">Super Admin Configuration</h2>
        </div>
        <p className="text-purple-100">Configure all platform settings without code changes</p>
      </div>

      {/* Section Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2">
        <div className="flex flex-wrap gap-2">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeSection === section.id
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`section-${section.id}`}
              >
                <Icon size={16} />
                {section.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Platform Settings */}
      {activeSection === 'platform' && platformConfig && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Globe className="text-purple-600" size={20} />
            Platform Settings
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Platform Name</label>
              <input
                type="text"
                value={platformConfig.platform_name || ''}
                onChange={(e) => setPlatformConfig({ ...platformConfig, platform_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                data-testid="platform-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tagline</label>
              <input
                type="text"
                value={platformConfig.tagline || ''}
                onChange={(e) => setPlatformConfig({ ...platformConfig, tagline: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Edition Number</label>
              <input
                type="number"
                value={platformConfig.edition || 1}
                onChange={(e) => setPlatformConfig({ ...platformConfig, edition: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                min="1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Venue</label>
              <input
                type="text"
                value={platformConfig.venue || ''}
                onChange={(e) => setPlatformConfig({ ...platformConfig, venue: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Event Start Date</label>
              <input
                type="date"
                value={platformConfig.event_dates?.start_date || ''}
                onChange={(e) => setPlatformConfig({ 
                  ...platformConfig, 
                  event_dates: { ...platformConfig.event_dates, start_date: e.target.value }
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Event End Date</label>
              <input
                type="date"
                value={platformConfig.event_dates?.end_date || ''}
                onChange={(e) => setPlatformConfig({ 
                  ...platformConfig, 
                  event_dates: { ...platformConfig.event_dates, end_date: e.target.value }
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Total Prize Pool (₹)</label>
              <input
                type="number"
                value={platformConfig.total_prize_pool || 0}
                onChange={(e) => setPlatformConfig({ ...platformConfig, total_prize_pool: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                min="0"
                step="10000"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={savePlatformConfig}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50"
              data-testid="save-platform-btn"
            >
              {saving ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Save size={18} />}
              Save Platform Settings
            </button>
          </div>
        </div>
      )}

      {/* Pricing & Fees */}
      {activeSection === 'pricing' && platformConfig && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <DollarSign className="text-green-600" size={20} />
            Pricing & Fees Configuration
          </h3>
          
          {/* Hackathon Pricing */}
          <div className="mb-8">
            <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Code size={16} className="text-blue-600" />
              Hackathon Entry Fees
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Workshop + Hackathon (₹)</label>
                <input
                  type="number"
                  value={platformConfig.pricing?.hackathon_only || 1200}
                  onChange={(e) => setPlatformConfig({
                    ...platformConfig,
                    pricing: { ...platformConfig.pricing, hackathon_only: parseInt(e.target.value) }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Workshop + Hackathon + 3 Days Accommodation (₹)</label>
                <input
                  type="number"
                  value={platformConfig.pricing?.hackathon_with_accommodation || 4000}
                  onChange={(e) => setPlatformConfig({
                    ...platformConfig,
                    pricing: { ...platformConfig.pricing, hackathon_with_accommodation: parseInt(e.target.value) }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Email Pricing */}
          <div className="mb-8">
            <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Bell size={16} className="text-orange-600" />
              Email Credits Pricing (for Companies)
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">100 Emails (₹)</label>
                <input
                  type="number"
                  value={platformConfig.email_pricing?.tier_100 || 500}
                  onChange={(e) => setPlatformConfig({
                    ...platformConfig,
                    email_pricing: { ...platformConfig.email_pricing, tier_100: parseInt(e.target.value) }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">1000 Emails (₹)</label>
                <input
                  type="number"
                  value={platformConfig.email_pricing?.tier_1000 || 2000}
                  onChange={(e) => setPlatformConfig({
                    ...platformConfig,
                    email_pricing: { ...platformConfig.email_pricing, tier_1000: parseInt(e.target.value) }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* Assessment Pricing */}
          <div className="mb-8">
            <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Building size={16} className="text-purple-600" />
              Company Assessment Hosting
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hosting Fee (₹)</label>
                <input
                  type="number"
                  value={platformConfig.assessment_pricing?.company_hosting_fee || 2000}
                  onChange={(e) => setPlatformConfig({
                    ...platformConfig,
                    assessment_pricing: { ...platformConfig.assessment_pricing, company_hosting_fee: parseInt(e.target.value) }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Validity (Months)</label>
                <input
                  type="number"
                  value={platformConfig.assessment_pricing?.validity_months || 6}
                  onChange={(e) => setPlatformConfig({
                    ...platformConfig,
                    assessment_pricing: { ...platformConfig.assessment_pricing, validity_months: parseInt(e.target.value) }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  min="1"
                  max="24"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={savePlatformConfig}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Save size={18} />}
              Save Pricing
            </button>
          </div>
        </div>
      )}

      {/* Domains & Workshops */}
      {activeSection === 'domains' && platformConfig && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Layers className="text-blue-600" size={20} />
            Domains & Workshops
          </h3>
          
          {/* Domains */}
          <div className="mb-8">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Hackathon Domains</h4>
            <div className="space-y-3">
              {(platformConfig.domains || []).map((domain, idx) => (
                <div key={domain.id || idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <button
                    onClick={() => {
                      const newDomains = [...platformConfig.domains];
                      newDomains[idx] = { ...domain, enabled: !domain.enabled };
                      setPlatformConfig({ ...platformConfig, domains: newDomains });
                    }}
                    className={`p-2 rounded-lg transition-colors ${domain.enabled ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-400'}`}
                  >
                    {domain.enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={domain.name || ''}
                      onChange={(e) => {
                        const newDomains = [...platformConfig.domains];
                        newDomains[idx] = { ...domain, name: e.target.value };
                        setPlatformConfig({ ...platformConfig, domains: newDomains });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Domain Name"
                    />
                  </div>
                  <div className="w-40">
                    <div className="flex items-center gap-2">
                      <Award size={16} className="text-yellow-600" />
                      <input
                        type="number"
                        value={domain.prize_pool || 0}
                        onChange={(e) => {
                          const newDomains = [...platformConfig.domains];
                          newDomains[idx] = { ...domain, prize_pool: parseInt(e.target.value) };
                          setPlatformConfig({ ...platformConfig, domains: newDomains });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Prize Pool"
                        min="0"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const newDomains = platformConfig.domains.filter((_, i) => i !== idx);
                      setPlatformConfig({ ...platformConfig, domains: newDomains });
                    }}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const newDomains = [...(platformConfig.domains || []), {
                    id: `domain_${Date.now()}`,
                    name: 'New Domain',
                    enabled: true,
                    prize_pool: 100000
                  }];
                  setPlatformConfig({ ...platformConfig, domains: newDomains });
                }}
                className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Plus size={18} />
                Add Domain
              </button>
            </div>
          </div>

          {/* Workshops */}
          <div className="mb-8">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Workshops</h4>
            <div className="space-y-3">
              {(platformConfig.workshops || []).map((workshop, idx) => (
                <div key={workshop.id || idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <button
                    onClick={() => {
                      const newWorkshops = [...platformConfig.workshops];
                      newWorkshops[idx] = { ...workshop, enabled: !workshop.enabled };
                      setPlatformConfig({ ...platformConfig, workshops: newWorkshops });
                    }}
                    className={`p-2 rounded-lg transition-colors ${workshop.enabled ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-400'}`}
                  >
                    {workshop.enabled ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                  </button>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={workshop.name || ''}
                      onChange={(e) => {
                        const newWorkshops = [...platformConfig.workshops];
                        newWorkshops[idx] = { ...workshop, name: e.target.value };
                        setPlatformConfig({ ...platformConfig, workshops: newWorkshops });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Workshop Name"
                    />
                  </div>
                  <button
                    onClick={() => {
                      const newWorkshops = platformConfig.workshops.filter((_, i) => i !== idx);
                      setPlatformConfig({ ...platformConfig, workshops: newWorkshops });
                    }}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const newWorkshops = [...(platformConfig.workshops || []), {
                    id: `workshop_${Date.now()}`,
                    name: 'New Workshop',
                    enabled: true
                  }];
                  setPlatformConfig({ ...platformConfig, workshops: newWorkshops });
                }}
                className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Plus size={18} />
                Add Workshop
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={savePlatformConfig}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Save size={18} />}
              Save Domains & Workshops
            </button>
          </div>
        </div>
      )}

      {/* Module Control */}
      {activeSection === 'modules' && platformConfig && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Zap className="text-yellow-600" size={20} />
            Module Control
          </h3>
          <p className="text-sm text-gray-500 mb-6">Enable or disable major platform modules</p>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { key: 'hackathon', label: 'Hackathon', icon: Code, desc: 'Main hackathon registration & submissions' },
              { key: 'iapl', label: 'AI League (IAPL)', icon: Brain, desc: "India's AI Premium League program" },
              { key: 'developer_engagement', label: 'Developer Engagement', icon: Users, desc: 'Coding competitions & challenges' },
              { key: 'company_assessments', label: 'Company Assessments', icon: Building, desc: 'Company-hosted assessments' }
            ].map((module) => {
              const Icon = module.icon;
              const isEnabled = platformConfig.modules_enabled?.[module.key] ?? false;
              return (
                <div
                  key={module.key}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    isEnabled ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Icon size={24} className={isEnabled ? 'text-green-600' : 'text-gray-400'} />
                      <span className="font-semibold text-gray-900">{module.label}</span>
                    </div>
                    <button
                      onClick={() => {
                        setPlatformConfig({
                          ...platformConfig,
                          modules_enabled: {
                            ...platformConfig.modules_enabled,
                            [module.key]: !isEnabled
                          }
                        });
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        isEnabled ? 'bg-green-200 text-green-700' : 'bg-gray-200 text-gray-500'
                      }`}
                      data-testid={`toggle-${module.key}`}
                    >
                      {isEnabled ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">{module.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={savePlatformConfig}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 disabled:opacity-50"
            >
              {saving ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Save size={18} />}
              Save Module Settings
            </button>
          </div>
        </div>
      )}

      {/* IAPL Configuration */}
      {activeSection === 'iapl' && iaplConfig && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Brain className="text-indigo-600" size={20} />
            India's AI Premium League (IAPL)
          </h3>
          
          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg mb-6">
            <div>
              <h4 className="font-semibold text-indigo-900">Enable IAPL Program</h4>
              <p className="text-sm text-indigo-600">Show IAPL in navigation and allow registrations</p>
            </div>
            <button
              onClick={() => setIaplConfig({ ...iaplConfig, enabled: !iaplConfig.enabled })}
              className={`p-2 rounded-lg transition-colors ${
                iaplConfig.enabled ? 'bg-green-200 text-green-700' : 'bg-gray-200 text-gray-500'
              }`}
            >
              {iaplConfig.enabled ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Program Name</label>
              <input
                type="text"
                value={iaplConfig.name || ''}
                onChange={(e) => setIaplConfig({ ...iaplConfig, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Short Name</label>
              <input
                type="text"
                value={iaplConfig.short_name || ''}
                onChange={(e) => setIaplConfig({ ...iaplConfig, short_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prize Pool (₹)</label>
              <input
                type="number"
                value={iaplConfig.prize_pool || 0}
                onChange={(e) => setIaplConfig({ ...iaplConfig, prize_pool: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Entry Fee (₹)</label>
              <input
                type="number"
                value={iaplConfig.entry_fee || 0}
                onChange={(e) => setIaplConfig({ ...iaplConfig, entry_fee: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                min="0"
              />
            </div>
          </div>

          {/* Timeline */}
          <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar size={16} />
              IAPL Timeline
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Online Training Start</label>
                <input
                  type="date"
                  value={iaplConfig.timeline?.online_training_start || ''}
                  onChange={(e) => setIaplConfig({
                    ...iaplConfig,
                    timeline: { ...iaplConfig.timeline, online_training_start: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Online Training End</label>
                <input
                  type="date"
                  value={iaplConfig.timeline?.online_training_end || ''}
                  onChange={(e) => setIaplConfig({
                    ...iaplConfig,
                    timeline: { ...iaplConfig.timeline, online_training_end: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Dev Start</label>
                <input
                  type="date"
                  value={iaplConfig.timeline?.product_dev_start || ''}
                  onChange={(e) => setIaplConfig({
                    ...iaplConfig,
                    timeline: { ...iaplConfig.timeline, product_dev_start: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Finals Date</label>
                <input
                  type="date"
                  value={iaplConfig.timeline?.finals_date || ''}
                  onChange={(e) => setIaplConfig({
                    ...iaplConfig,
                    timeline: { ...iaplConfig.timeline, finals_date: e.target.value }
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={saveIaplConfig}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : <Save size={18} />}
              Save IAPL Settings
            </button>
          </div>
        </div>
      )}

      {/* Banners */}
      {activeSection === 'banners' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Megaphone className="text-orange-600" size={20} />
              Banner Advertisements
            </h3>
            <button
              onClick={() => { resetBannerForm(); setEditingBanner(null); setShowBannerModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700"
              data-testid="create-banner-btn"
            >
              <Plus size={18} />
              Create Banner
            </button>
          </div>

          {banners.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Megaphone className="mx-auto text-gray-300 mb-3" size={48} />
              <p className="text-gray-500">No banners configured</p>
              <p className="text-sm text-gray-400">Create banners to show announcements across dashboards</p>
            </div>
          ) : (
            <div className="space-y-4">
              {banners.map((banner) => (
                <div
                  key={banner.id}
                  className={`p-4 rounded-lg border-2 ${
                    banner.is_active ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          banner.type === 'promo' ? 'bg-purple-100 text-purple-700' :
                          banner.type === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                          banner.type === 'success' ? 'bg-green-100 text-green-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {banner.type}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          banner.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {banner.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <h4 className="font-semibold text-gray-900">{banner.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{banner.message}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {banner.start_date} to {banner.end_date}
                        </span>
                        {banner.cta_link && (
                          <span className="flex items-center gap-1">
                            <Link size={12} />
                            {banner.cta_text}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditBanner(banner)}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteBanner(banner.id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Institution Logos Management */}
      {activeSection === 'institution-logos' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="institution-logos-section">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <ImageIcon className="text-blue-600" size={20} />
              Institution Logos
            </h3>
            <button
              onClick={() => { setEditingLogo(null); setLogoForm({ logo_url: '', alt_text: '' }); setShowLogoModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              data-testid="add-logo-btn"
            >
              <Plus size={16} /> Add Logo
            </button>
          </div>
          <p className="text-gray-500 text-sm mb-6">Manage institution logos displayed on the Colleges Landing Page. Only active logos are visible publicly.</p>

          {institutionLogos.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
              <ImageIcon className="mx-auto text-gray-300 mb-3" size={40} />
              <p className="text-gray-500 font-medium">No logos added yet</p>
              <p className="text-gray-400 text-sm mt-1">Add institution logos to display on the landing page</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {institutionLogos.map((logo) => (
                <div key={logo.id} className="relative group border border-gray-200 rounded-xl p-4 flex flex-col items-center hover:border-blue-300 transition-colors" data-testid={`logo-card-${logo.id}`}>
                  <div className="h-20 w-full flex items-center justify-center mb-3">
                    <img src={logo.logo_url} alt={logo.alt_text || 'Logo'} className="max-h-full max-w-full object-contain" />
                  </div>
                  {logo.alt_text && <p className="text-xs text-gray-500 text-center truncate w-full">{logo.alt_text}</p>}
                  <div className="flex items-center gap-1 mt-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${logo.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {logo.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </div>
                  {/* Actions overlay */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => { setEditingLogo(logo); setLogoForm({ logo_url: logo.logo_url, alt_text: logo.alt_text || '' }); setShowLogoModal(true); }}
                      className="p-1.5 bg-white border border-gray-200 rounded-lg text-blue-600 hover:bg-blue-50 shadow-sm"
                      data-testid={`edit-logo-${logo.id}`}
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm('Delete this logo?')) return;
                        try {
                          await axios.delete(`${API}/admin/super-config/institution-logos/${logo.id}`, { headers: { Authorization: `Bearer ${token}` } });
                          setInstitutionLogos(prev => prev.filter(l => l.id !== logo.id));
                          toast.success('Logo deleted');
                        } catch { toast.error('Failed to delete logo'); }
                      }}
                      className="p-1.5 bg-white border border-gray-200 rounded-lg text-red-600 hover:bg-red-50 shadow-sm"
                      data-testid={`delete-logo-${logo.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Logo Add/Edit Modal */}
      {showLogoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-xl">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">{editingLogo ? 'Edit Logo' : 'Add Logo'}</h3>
              <button onClick={() => setShowLogoModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Logo URL <span className="text-red-500">*</span></label>
                <input
                  type="url"
                  value={logoForm.logo_url}
                  onChange={(e) => setLogoForm({ ...logoForm, logo_url: e.target.value })}
                  placeholder="https://example.com/logo.png"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                  data-testid="logo-url-input"
                />
              </div>
              {logoForm.logo_url && (
                <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-center border border-gray-200">
                  <img src={logoForm.logo_url} alt="Preview" className="max-h-20 max-w-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Alt Text (optional)</label>
                <input
                  type="text"
                  value={logoForm.alt_text}
                  onChange={(e) => setLogoForm({ ...logoForm, alt_text: e.target.value })}
                  placeholder="Institution name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
                  data-testid="logo-alt-input"
                />
              </div>
            </div>
            <div className="p-5 border-t border-gray-200 flex gap-3">
              <button onClick={() => setShowLogoModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
              <button
                disabled={!logoForm.logo_url.trim() || logoSaving}
                onClick={async () => {
                  setLogoSaving(true);
                  try {
                    if (editingLogo) {
                      await axios.put(`${API}/admin/super-config/institution-logos/${editingLogo.id}`, logoForm, { headers: { Authorization: `Bearer ${token}` } });
                      setInstitutionLogos(prev => prev.map(l => l.id === editingLogo.id ? { ...l, ...logoForm } : l));
                      toast.success('Logo updated');
                    } else {
                      const res = await axios.post(`${API}/admin/super-config/institution-logos`, logoForm, { headers: { Authorization: `Bearer ${token}` } });
                      setInstitutionLogos(prev => [...prev, res.data.logo]);
                      toast.success('Logo added');
                    }
                    setShowLogoModal(false);
                  } catch { toast.error('Failed to save logo'); }
                  finally { setLogoSaving(false); }
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                data-testid="save-logo-btn"
              >
                {logoSaving ? 'Saving...' : editingLogo ? 'Update' : 'Add Logo'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Access Control */}
      {activeSection === 'roles' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Shield className="text-red-600" size={20} />
            Role-Based Access Control
          </h3>
          
          {/* Role Definitions */}
          <div className="mb-8">
            <h4 className="text-md font-semibold text-gray-800 mb-4">Role Definitions</h4>
            <div className="grid md:grid-cols-2 gap-4">
              {Object.entries(adminRoles.role_definitions || {}).map(([key, role]) => (
                <div key={key} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield size={16} className="text-purple-600" />
                    <span className="font-semibold text-gray-900">{role.name}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{role.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {role.permissions?.map((perm, idx) => (
                      <span key={idx} className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                        {perm}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Current Admins */}
          <div>
            <h4 className="text-md font-semibold text-gray-800 mb-4">Admin Accounts</h4>
            <div className="space-y-3">
              {adminRoles.admins?.map((admin) => (
                <div key={admin.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-semibold text-gray-900">{admin.username}</span>
                    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                      admin.role === 'super_admin' ? 'bg-red-100 text-red-700' :
                      admin.role === 'program_admin' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {admin.role || 'super_admin'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Banner Modal */}
      {showBannerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                {editingBanner ? 'Edit Banner' : 'Create Banner'}
              </h3>
              <button
                onClick={() => { setShowBannerModal(false); setEditingBanner(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={bannerForm.title}
                  onChange={(e) => setBannerForm({ ...bannerForm, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Banner title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea
                  value={bannerForm.message}
                  onChange={(e) => setBannerForm({ ...bannerForm, message: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg min-h-[80px]"
                  placeholder="Banner message"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select
                    value={bannerForm.type}
                    onChange={(e) => setBannerForm({ ...bannerForm, type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="success">Success</option>
                    <option value="promo">Promo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <input
                    type="number"
                    value={bannerForm.priority}
                    onChange={(e) => setBannerForm({ ...bannerForm, priority: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    min="1"
                    max="10"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date *</label>
                  <input
                    type="date"
                    value={bannerForm.start_date}
                    onChange={(e) => setBannerForm({ ...bannerForm, start_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
                  <input
                    type="date"
                    value={bannerForm.end_date}
                    onChange={(e) => setBannerForm({ ...bannerForm, end_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Text (optional)</label>
                <input
                  type="text"
                  value={bannerForm.cta_text}
                  onChange={(e) => setBannerForm({ ...bannerForm, cta_text: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Learn More"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <input
                  type="url"
                  value={bannerForm.cta_link}
                  onChange={(e) => setBannerForm({ ...bannerForm, cta_link: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="https://..."
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={bannerForm.is_active}
                  onChange={(e) => setBannerForm({ ...bannerForm, is_active: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">Active</label>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => { setShowBannerModal(false); setEditingBanner(null); }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBanner}
                disabled={!bannerForm.title || !bannerForm.message || !bannerForm.start_date || !bannerForm.end_date}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50"
              >
                {editingBanner ? 'Update' : 'Create'} Banner
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminConfigPanel;
